import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation, Trans } from 'react-i18next'

import TimerSVG from '@krea/common/images/svg/timer.svg'
import { ADVICE_CATEGORY, LOAN_APPLICATION_STATES } from '@krea/common/utils'
import { Toast, ToastWithContactPerson, TOAST_TYPE } from '@krea/common/shared-components'
import { useGetCachedCurrentLoanApplication } from '@krea/common/store/current-loan-application/hooks'

const {
	CREATED,
	ADVICE,
	OPEN_FOR_BIDS,
	REJECTED,
	SUBJECT_TO_EU_SANCTIONS,
	NO_BIDS_RECEIVED,
	BIDS_REJECTED_BY_CUSTOMER
} = LOAN_APPLICATION_STATES

const LoanApplicationMessages = ({ hasBids, isSubmitApplicationModal, bidsCount }) => {
	const { t } = useTranslation()
	const [toastProps, setToastProps] = useState(null)
	const [toastType, setToastType] = useState(TOAST_TYPE.NORMAL_TOAST)
	const { data: currentLoanApplication = {} } = useGetCachedCurrentLoanApplication()

	const { state, isDigitalAdvice, advisoryAcceptedAt, adviceCategory, biddingEndsAt } = currentLoanApplication

	const biddingClosed = biddingEndsAt ? dayjs(biddingEndsAt).isBefore(dayjs()) : false
	const lenderHasUntilHumanizedFormat = t('dash.lenderHasUntilHumanizedFormat')
	const biddingEndsAtHumanized = biddingEndsAt ? dayjs(biddingEndsAt).format(lenderHasUntilHumanizedFormat) : null

	const contactCustomerService = (
		<Trans i18nKey="dash.bidsRejected.paragraphWithLinktoSupport">
			Placeholder for first text
			{/*eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
			<a
				href="#"
				className="font-weight-bold"
				onClick={(e) => {
					e.preventDefault()
					window.MessageBirdChatWidget &&
						!window.MessageBirdChatWidget.isOpen &&
						window.MessageBirdChatWidget.toggleChat()
				}}
			>
				Placeholder for second text
			</a>
		</Trans>
	)

	useEffect(() => {
		switch (state) {
			case CREATED:
				setToastProps({
					isWarning: true,
					innerText: {
						title: t('dash.messages.applicationNotSubmitted.title'),
						subtitle: t('dash.messages.applicationNotSubmitted.subtitle')
					}
				})

				if (!isSubmitApplicationModal) {
					setToastType(TOAST_TYPE.TOAST_WITH_CONTACT_PERSON)
				}

				break
			case ADVICE:
				const innerText = {}
				const disabledIconSmallScreens = true

				if (isDigitalAdvice) {
					if (isSubmitApplicationModal) {
						innerText.title = t('dash.messages.applicationDigitalAdvice.title')
						innerText.subtitle = t('dash.messages.applicationDigitalAdvice.subtitle')
					} else {
						innerText.title = t('iaq.applicationPreview.popup.title.applicationDigitalAdvice')
						innerText.subtitle = t('dash.messages.applicationDigitalAdvice.subtitleAdvicePage')
					}
				} else if (advisoryAcceptedAt) {
					innerText.title = t('dash.messages.applicationAdviceWaiting.title')
					if (adviceCategory === ADVICE_CATEGORY.COMPLEX) {
						innerText.title = t('dash.messages.applicationAdvice.title')
						innerText.subtitle = t('dash.messages.applicationAdviceWaiting.title')
					}
				} else {
					innerText.title = t('dash.messages.applicationAdvice.title')

					if (!isSubmitApplicationModal && adviceCategory !== ADVICE_CATEGORY.COMPLEX) {
						innerText.subtitle = t('dash.messages.applicationAdvice.subtitle')
					}
				}

				setToastProps({ innerText, disabledIconSmallScreens })
				break
			case OPEN_FOR_BIDS:
				if (!biddingClosed && biddingEndsAtHumanized) {
					setToastProps({
						icon: TimerSVG,
						innerText: {
							title: t('dash.applicationHasBeenSent'),
							subtitle: t('dash.lenderHasUntil', { biddingEndsAtHumanized })
						}
					})

					if (!isSubmitApplicationModal) {
						setToastType(TOAST_TYPE.TOAST_WITH_CONTACT_PERSON)
					}
				} else if (biddingClosed && !hasBids) {
					setToastProps({
						isWarning: true,
						innerText: { title: t('dash.noBidsMessage'), subtitle: t('dash.noBidsImproveApplicationRecommend') }
					})
				} else if (biddingClosed && hasBids && bidsCount) {
					setToastProps({
						excludeIcon: true,
						excludeBorder: true,
						innerText: { title: t('dash.bidsReceived.title', { bidsCount }), subtitle: t('dash.bidsReceived.subtitle') }
					})

					if (!isSubmitApplicationModal) {
						setToastType(TOAST_TYPE.TOAST_WITH_CONTACT_PERSON)
					}
				}

				break
			case REJECTED:
			case SUBJECT_TO_EU_SANCTIONS:
				setToastProps({
					isWarning: true,
					innerText: {
						title: `${t('dash.rejectedOrSanctionsState.heading')}.`,
						subtitle: t('dash.rejectedOrSanctionsMessage')
					}
				})

				if (!isSubmitApplicationModal) {
					setToastType(TOAST_TYPE.TOAST_WITH_CONTACT_PERSON)
				}

				break
			case NO_BIDS_RECEIVED:
				setToastProps({
					isWarning: true,
					innerText: { title: t('dash.noBidsMessage'), subtitle: t('dash.noBidsImproveApplicationRecommend') }
				})

				break
			case BIDS_REJECTED_BY_CUSTOMER:
				setToastProps({
					innerText: {
						title: t('dash.bidsRejected.heading'),
						subtitle: contactCustomerService
					}
				})

				break
			default:
				break
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state])

	if (!toastProps) {
		return null
	} else if (toastType === TOAST_TYPE.TOAST_WITH_CONTACT_PERSON) {
		return <ToastWithContactPerson {...toastProps} />
	}

	return <Toast {...toastProps} />
}

export default LoanApplicationMessages
